import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, User, Package, Clock, MapPin, Eye, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn, formatDate } from '../lib/utils';

const CalendarPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Event details modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [eventFullData, setEventFullData] = useState(null);
  const [loadingEventDetails, setLoadingEventDetails] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const response = await api().get('/calendar/events');
      setEvents(response.data);
    } catch (error) {
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
  const startDayOfWeek = monthStart.getDay();
  
  // Create padding for days before the first of the month
  const paddingDays = Array(startDayOfWeek).fill(null);

  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return isSameDay(eventDate, date);
    });
  };

  const selectedDayEvents = selectedDate ? getEventsForDay(selectedDate) : [];

  const eventTypeColors = {
    entrada: 'bg-emerald-500',
    salida: 'bg-red-500',
    retorno: 'bg-blue-500',
    evento: 'bg-primary'
  };
  
  // Handle click on event to view details
  const handleEventClick = async (event) => {
    setSelectedEvent(event);
    setEventDetailsOpen(true);
    setLoadingEventDetails(true);
    
    try {
      // Get movement_id from event
      const movementId = event.extendedProps?.movement_id || event.id;
      if (movementId) {
        const response = await api().get(`/movements/${movementId}`);
        setEventFullData(response.data);
      }
    } catch (error) {
      console.error('Error loading event details:', error);
      // Still show what we have from the event
      setEventFullData(null);
    } finally {
      setLoadingEventDetails(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Calendario</h1>
          <p className="text-muted-foreground mt-1">
            Vista de movimientos y eventos programados
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">
              {format(currentDate, 'MMMM yyyy', { locale: es })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Hoy
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-96 w-full" />
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {/* Day headers */}
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
                  <div
                    key={day}
                    className="text-center text-sm font-medium text-muted-foreground py-2"
                  >
                    {day}
                  </div>
                ))}
                
                {/* Padding days */}
                {paddingDays.map((_, index) => (
                  <div key={`padding-${index}`} className="aspect-square" />
                ))}
                
                {/* Calendar days */}
                {daysInMonth.map((day) => {
                  const dayEvents = getEventsForDay(day);
                  const isToday = isSameDay(day, new Date());
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={cn(
                        'aspect-square p-1 rounded-lg transition-colors relative',
                        'hover:bg-muted/50',
                        isToday && 'bg-primary/10 border border-primary/30',
                        isSelected && 'bg-primary/20 border border-primary'
                      )}
                    >
                      <div className={cn(
                        'text-sm font-medium',
                        isToday && 'text-primary'
                      )}>
                        {format(day, 'd')}
                      </div>
                      {dayEvents.length > 0 && (
                        <div className="absolute bottom-1 left-1 right-1 flex gap-0.5 justify-center">
                          {dayEvents.slice(0, 3).map((event, idx) => (
                            <div
                              key={idx}
                              className={cn(
                                'w-1.5 h-1.5 rounded-full',
                                eventTypeColors[event.extendedProps?.type] || 'bg-gray-500'
                              )}
                            />
                          ))}
                          {dayEvents.length > 3 && (
                            <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Events List */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              {selectedDate ? (
                format(selectedDate, "d 'de' MMMM", { locale: es })
              ) : (
                'Selecciona un día'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedDate ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Haz clic en un día para ver los eventos</p>
              </div>
            ) : selectedDayEvents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No hay eventos para este día</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDayEvents.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    className={cn(
                      'w-full text-left p-3 rounded-lg border border-border/40',
                      'hover:border-primary/30 hover:bg-muted/30 transition-colors cursor-pointer'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          'w-3 h-3 rounded-full mt-1.5',
                          eventTypeColors[event.extendedProps?.type] || 'bg-gray-500'
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{event.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {event.extendedProps?.type || 'evento'}
                          </Badge>
                          {event.extendedProps?.responsible && (
                            <span className="text-xs text-muted-foreground truncate">
                              {event.extendedProps.responsible}
                            </span>
                          )}
                        </div>
                        {event.extendedProps?.notes && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {event.extendedProps.notes}
                          </p>
                        )}
                        <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                          <Eye className="h-3 w-3" />
                          Ver detalles
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Legend */}
      <Card className="card-hover">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm text-muted-foreground font-medium">Leyenda:</span>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-sm">Entradas</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-sm">Salidas</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-sm">Retornos</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary" />
              <span className="text-sm">Eventos</span>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Event Details Modal */}
      <Dialog open={eventDetailsOpen} onOpenChange={setEventDetailsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              Detalles del Evento
            </DialogTitle>
            <DialogDescription>
              Información completa del movimiento
            </DialogDescription>
          </DialogHeader>
          
          {loadingEventDetails ? (
            <div className="py-8 text-center text-muted-foreground">
              <RefreshCw className="h-6 w-6 mx-auto animate-spin mb-2" />
              Cargando detalles...
            </div>
          ) : eventFullData ? (
            <div className="space-y-4 py-4">
              {/* General Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Evento/Destino</Label>
                  <p className="font-medium">{eventFullData.event_name}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">No. Control</Label>
                  <p className="font-mono">{eventFullData.control_number || 'Sin asignar'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tipo</Label>
                  <Badge>{eventFullData.type || 'salida'}</Badge>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Cliente</Label>
                  <p>{eventFullData.client_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Responsable</Label>
                  <p className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {eventFullData.responsible_name}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fecha/Hora Evento</Label>
                  <p className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDate(eventFullData.event_date)} {eventFullData.event_time || ''}
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fecha Registro</Label>
                  <p>{eventFullData.created_at ? formatDate(eventFullData.created_at) : '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Estado</Label>
                  <Badge variant={eventFullData.has_return ? 'default' : 'warning'}>
                    {eventFullData.has_return ? 'Retornado' : 'Pendiente de retorno'}
                  </Badge>
                </div>
              </div>
              
              {/* Equipment List */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Equipos ({eventFullData.items?.length || 0})</Label>
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {eventFullData.items?.map((item, idx) => (
                    <div key={idx} className="p-3 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-primary" />
                        <div>
                          <p className="font-medium">{item.equipment_name}</p>
                          <p className="text-xs text-muted-foreground">
                            Serial: {item.serial_numbers || 'Auto-asignado'} | Ubicación: {item.location_name || '-'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline">x{item.quantity}</Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              {eventFullData.notes && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Notas</Label>
                  <p className="text-sm bg-muted/30 p-2 rounded">{eventFullData.notes}</p>
                </div>
              )}
              
              {/* Emergency indicator */}
              {eventFullData.equipment_no_review && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm text-red-400 font-medium flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Salida de Emergencia (Bomberazo)
                  </p>
                </div>
              )}
            </div>
          ) : selectedEvent ? (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Título</Label>
                  <p className="font-medium">{selectedEvent.title}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Tipo</Label>
                  <Badge>{selectedEvent.extendedProps?.type || 'evento'}</Badge>
                </div>
                {selectedEvent.extendedProps?.responsible && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Responsable</Label>
                    <p>{selectedEvent.extendedProps.responsible}</p>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fecha</Label>
                  <p>{format(new Date(selectedEvent.start), "d 'de' MMMM yyyy", { locale: es })}</p>
                </div>
              </div>
              {selectedEvent.extendedProps?.notes && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Notas</Label>
                  <p className="text-sm bg-muted/30 p-2 rounded">{selectedEvent.extendedProps.notes}</p>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
